"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  columnNames,
  DatasetInput,
  useDatasetInput,
} from "@/components/tools/stats-regression/dataset-input";
import { LogisticResults } from "@/components/tools/stats-regression/logistic-results";
import { useModelVariables } from "@/components/tools/stats-regression/use-model-variables";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type DataColumn,
  type Dataset,
  defaultPositiveLevel,
} from "@/lib/tool-kit/stats-regression/dataset";
import { buildDesign, codingOf } from "@/lib/tool-kit/stats-regression/design";
import { logisticReport } from "@/lib/tool-kit/stats-regression/logistic";

/** English strings. A translated content file overrides these through `ui`. */
export const LOGISTIC_REGRESSION_CALCULATOR_STRINGS = {
  dataLabel: "Data — one row per case, one column per variable",
  dataHint:
    "Paste from a spreadsheet (with a header row of variable names), or separate values with tabs, commas, semicolons or spaces. The outcome can be 0/1 or any two labels such as pass/fail. Blank cells and NA count as missing.",
  placeholder: "hours\tpass\n0.5\t0\n3.25\t1",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  readSummary: "Read {rows} × {columns}.",
  rowCount: "one: {count} row|other: {count} rows",
  columnCount: "one: {count} column|other: {count} columns",
  moreRows: "one: … and {count} more row|other: … and {count} more rows",
  missingCells:
    "one: {count} cell is blank or NA.|other: {count} cells are blank or NA.",
  variableName: "Variable {number}",
  example:
    "hours\tpass\n0.5\t0\n0.75\t0\n1\t0\n1.25\t0\n1.5\t0\n1.75\t0\n1.75\t1\n2\t0\n2.25\t1\n2.5\t0\n2.75\t1\n3\t0\n3.25\t1\n3.5\t0\n4\t1\n4.25\t1\n4.5\t1\n4.75\t1\n5\t1\n5.5\t1",
  outcomeLabel: "Outcome (Y) — a variable with exactly two values",
  eventLabel: "Event: the value coded 1",
  predictorsLabel: "Predictors (X)",
  levelLabel: "Confidence level",
  level90: "90%",
  level95: "95%",
  level99: "99%",
  cutoffLabel: "Classification cutoff",
  cutoffHint:
    "A case is classed as the event when its predicted probability is at least this.",
  firthLabel:
    "Use Firth's penalised likelihood (for separation or small samples)",
  binaryTerm: "{name} [{level}]",
  needOutcome:
    "Paste data with a column that has exactly two different values to use as the outcome.",
  needPredictors: "Tick at least one predictor.",
  tooFew:
    "Not enough complete cases, or the outcome has only one value among them. Logistic regression needs both outcomes present and more cases than predictors.",
  droppedRows:
    "one: {count} row with a missing value was left out (listwise deletion).|other: {count} rows with missing values were left out (listwise deletion).",
  results: "Results",
  separationTitle: "Separation detected: these estimates are not reliable",
  separation:
    "Some fitted probabilities are numerically 0 or 1. That usually means a predictor (or combination of predictors) separates the two outcomes perfectly or almost perfectly, so the likelihood has no maximum: the coefficients would grow without limit, the standard errors and Wald p-values below are meaningless, and the odds ratios are not real. Do not report them. Options: tick Firth's penalised likelihood above, which gives finite estimates; merge sparse categories; remove or recode the offending predictor; or collect more data.",
  noConvergence:
    "The fit did not converge within the iteration limit, so the estimates are unreliable. Check for separation, very large predictor values (rescale them), or near-duplicate predictors.",
  firthInfo:
    "Firth's penalised likelihood was used. Estimates are finite even under separation. The standard errors and p-values are Wald values; profile penalised-likelihood intervals, as in R's logistf, are usually better and are not computed here. Deviance, AIC, BIC and the pseudo-R² values use the ordinary likelihood evaluated at the Firth estimates.",
  firthConverged:
    "The Firth fit did not converge within 100 iterations; treat the estimates with caution.",
  evpWarning:
    "Only {events} events per predictor. With fewer than about 10 the estimates are unstable and biased away from zero; fewer predictors, or Firth's method, help.",
  lrChi2: "Likelihood-ratio χ²({df})",
  lrChi2Firth: "Penalised LR χ²({df})",
  lrP: "p-value of the model",
  mcFadden: "McFadden R²",
  nagelkerke: "Nagelkerke R²",
  auc: "AUC",
  accuracy: "Accuracy at the cutoff",
  casesN: "Cases (events / non-events)",
  casesValue: "{n} ({events} / {nonEvents})",
  summary:
    "The model predicts {y} = {event} significantly better than a model with no predictors (p {p}); it separates events from non-events with an AUC of {auc}.",
  summaryNot:
    "The model does not predict {y} = {event} significantly better than a model with no predictors (p {p}); its AUC is {auc}.",
  equationTitle: "Fitted model (log-odds)",
  equationIntercept: "ln(odds of {event}) = {a}",
  equationPlus: " + {b} × {name}",
  equationMinus: " − {b} × {name}",
  coefficientsTitle: "Coefficients and odds ratios",
  colTerm: "Term",
  colB: "B",
  colSe: "SE B",
  colZ: "Wald z",
  colP: "p",
  colOr: "Odds ratio",
  colOrCi: "{level} CI for the odds ratio",
  termIntercept: "Intercept",
  ciTemplate: "{low} to {high}",
  aliasedTerm: "aliased",
  orNote:
    "The odds ratio is exp(B): the factor by which the odds of the event are multiplied for a one-unit increase in the predictor (or for the coded level of a 0/1 predictor), holding the others fixed. The interval is the Wald interval, exp(B ± z × SE) — the same as R's confint.default and the interval SPSS and Stata report.",
  apaTitle: "APA-style summary",
  apaModel:
    "A logistic regression predicting {y} from {predictors} was {verdict}, χ²({df}, N = {n}) = {chi}, p {p}, Nagelkerke R² = {r2}.",
  apaVerdictSig: "statistically significant",
  apaVerdictNot: "not statistically significant",
  apaTerm: "{name}: OR = {or}, {level} CI [{low}, {high}], p {p}",
  predictorSeparator: ", ",
  copyResult: "Copy summary",
  copyTable: "Copy table",
  copied: "Copied!",
  fitTitle: "Model fit",
  colMeasure: "Measure",
  colValue: "Value",
  rowDeviance: "Deviance (−2 log-likelihood)",
  rowNullDeviance: "Null deviance (no predictors)",
  rowAic: "AIC",
  rowBic: "BIC",
  rowCoxSnell: "Cox–Snell R²",
  rowTjur: "Tjur's R²",
  rowIterations: "IRLS iterations",
  fitNote:
    "McFadden, Cox–Snell, Nagelkerke and Tjur are different pseudo-R² measures; none is the share of variance explained. Report which one you use.",
  classTitle: "Classification at the cutoff {cutoff}",
  colActual: "Actual",
  colPredictedEvent: "Predicted event",
  colPredictedNon: "Predicted non-event",
  rowEvent: "Event ({event})",
  rowNonEvent: "Non-event",
  sensitivity: "Sensitivity",
  specificity: "Specificity",
  ppv: "Positive predictive value",
  npv: "Negative predictive value",
  rocTitle: "ROC curve",
  rocLabel: "ROC curve, area under the curve {auc}",
  axisFpr: "False-positive rate (1 − specificity)",
  axisTpr: "True-positive rate (sensitivity)",
  seriesRoc: "Model",
  curveTitle: "Fitted probability curve",
  curveLabel:
    "Fitted probability of {event} against {x}, with the observed outcomes",
  seriesObserved: "Observed outcomes (1 = {event})",
  seriesCurve: "Fitted probability",
  axisProbability: "Probability",
  predictTitle: "Predict the probability for new values",
  predictedProbability: "Predicted probability",
  predictedOdds: "Predicted odds",
  predictedInterval: "{level} CI for the probability",
  fillMeans: "Fill with the means",
  workingTitle: "How it was calculated",
  workIrls:
    "Iteratively reweighted least squares (R's glm algorithm): starting from μ = (y + ½) ÷ 2, each pass regresses the working response z = η + (y − μ) ÷ (μ(1 − μ)) on the predictors with weights μ(1 − μ), by QR, until the deviance changes by less than 1 part in 10⁸.",
  workIterations: "Converged after {count} iterations.",
  workFirth:
    "Firth's method maximises ℓ(β) + ½ log|XᵀWX| by Fisher scoring on the modified score X′(y − μ + h(½ − μ)), where h are the hat-matrix diagonals; it converged after {count} iterations.",
  workLr:
    "Likelihood-ratio test: χ² = null deviance − deviance = {null} − {dev} = {chi}, on {df} degrees of freedom.",
  workLrFirth:
    "Penalised likelihood-ratio test: χ² = 2 × (penalised log-likelihood of the model − that of the intercept-only Firth fit) = {chi}, on {df} degrees of freedom.",
  workNagelkerke:
    "Nagelkerke R² = (1 − exp(2(ℓ₀ − ℓ) ÷ n)) ÷ (1 − exp(2ℓ₀ ÷ n)) = {r2}",
} as const;

export type LogisticStrings = typeof LOGISTIC_REGRESSION_CALCULATOR_STRINGS;

const LEVELS = { "0.9": 0.9, "0.95": 0.95, "0.99": 0.99 } as const;
type LevelKey = keyof typeof LEVELS;

/** The level coded 1: the reader's pick if it still exists, else the default. */
function chosenEvent(outcome: DataColumn | null, choice: string): string {
  if (outcome === null) {
    return "";
  }
  return outcome.levels.includes(choice)
    ? choice
    : defaultPositiveLevel(outcome);
}

/** The 0/1 outcome for each row: null when the cell is blank. */
function outcomeColumn(
  dataset: Dataset,
  index: number,
  event: string
): (number | null)[] {
  return dataset.columns[index].raw.map((cell) => {
    if (cell === "") {
      return null;
    }
    return cell === event ? 1 : 0;
  });
}

function fitModel(
  dataset: Dataset,
  vars: { yIndex: number; predictors: number[] },
  event: string,
  names: string[],
  level: number,
  firth: boolean
) {
  if (vars.yIndex < 0 || vars.predictors.length === 0) {
    return null;
  }
  const design = buildDesign(
    outcomeColumn(dataset, vars.yIndex, event),
    vars.predictors.map((i) => dataset.columns[i])
  );
  if (design === null) {
    return null;
  }
  const report = logisticReport(names, design.predictors, design.y, {
    level,
    firth,
  });
  return { design, report };
}

export default function LogisticRegressionCalculatorWidget() {
  const strings = useToolUi(LOGISTIC_REGRESSION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const input = useDatasetInput();
  const [levelKey, setLevelKey] = useState<LevelKey>("0.95");
  const [firth, setFirth] = useState(false);
  const [cutoffText, setCutoffText] = useState("0.5");
  const [eventChoice, setEventChoice] = useState("");
  const { dataset } = input;
  const names = columnNames(dataset, strings.variableName);
  const vars = useModelVariables(
    dataset,
    (column) => column.levels.length === 2
  );

  const outcome = vars.yIndex >= 0 ? dataset.columns[vars.yIndex] : null;
  const event = chosenEvent(outcome, eventChoice);
  const termNames = vars.predictors.map((i) => {
    const positive = codingOf(dataset.columns[i]).positive;
    return positive === null
      ? names[i]
      : t(strings.binaryTerm, { name: names[i], level: positive });
  });
  const level = LEVELS[levelKey];

  const fit = fitModel(
    dataset,
    vars,
    event,
    [strings.termIntercept, ...termNames],
    level,
    firth
  );

  const cutoff = parseNumberInput(cutoffText);
  const options = vars.responses.map((i) => ({
    value: String(i),
    label: names[i],
  }));

  return (
    <>
      <DatasetInput
        example={strings.example}
        fmt={fmt}
        id="lg-data"
        labels={strings}
        locale={locale}
        state={input}
      />

      {outcome ? (
        <section className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField
              id="lg-outcome"
              label={strings.outcomeLabel}
              onChange={(value) => {
                vars.setYChoice(value);
                setEventChoice("");
              }}
              options={options}
              value={String(vars.yIndex)}
            />
            <SelectField
              id="lg-event"
              label={strings.eventLabel}
              onChange={setEventChoice}
              options={outcome.levels.map((value) => ({ value, label: value }))}
              value={event}
            />
            <SelectField
              id="lg-level"
              label={strings.levelLabel}
              onChange={setLevelKey}
              options={[
                { value: "0.9", label: strings.level90 },
                { value: "0.95", label: strings.level95 },
                { value: "0.99", label: strings.level99 },
              ]}
              value={levelKey}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.predictorsLabel}</legend>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {vars.candidates.map((i) => (
                <Checkbox
                  checked={vars.predictors.includes(i)}
                  key={i}
                  label={names[i]}
                  onChange={() => vars.toggle(i)}
                />
              ))}
            </div>
          </fieldset>
          <div className="grid items-end gap-4 sm:grid-cols-2">
            <TextField
              hint={strings.cutoffHint}
              id="lg-cutoff"
              label={strings.cutoffLabel}
              numeric
              onChange={setCutoffText}
              value={cutoffText}
            />
            <Checkbox
              checked={firth}
              label={strings.firthLabel}
              onChange={setFirth}
            />
          </div>
          {fit && fit.design.dropped > 0 ? (
            <Notice>
              {tPlural(strings.droppedRows, locale, fit.design.dropped, {
                count: fmt(fit.design.dropped),
              }).trim()}
            </Notice>
          ) : null}
        </section>
      ) : null}

      {dataset.columns.length > 0 && !outcome ? (
        <Notice>{strings.needOutcome}</Notice>
      ) : null}
      {outcome && vars.predictors.length === 0 ? (
        <Notice>{strings.needPredictors}</Notice>
      ) : null}
      {fit && fit.report === null ? <Notice>{strings.tooFew}</Notice> : null}

      {fit?.report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <LogisticResults
            cutoff={cutoff !== null && cutoff > 0 && cutoff < 1 ? cutoff : 0.5}
            design={fit.design}
            event={event}
            fmt={fmt}
            outcomeName={names[vars.yIndex]}
            report={fit.report}
            strings={strings}
            termNames={termNames}
          />
        </section>
      ) : null}
    </>
  );
}
